/**
 * Group Service
 * =============
 * Handles client group management operations for bulk assignment.
 */

import { supabase } from '../lib/supabase'
import type {
  Group,
  GroupInsert,
  GroupUpdate,
  GroupMember,
  GroupMemberInsert,
  Client,
} from '../types/database.types'

export interface GroupWithMembers extends Group {
  members: (GroupMember & { client: Pick<Client, 'id' | 'full_name' | 'email' | 'status'> })[]
  member_count: number
}

export interface GroupFilters {
  category?: string
  search?: string
}

export const groupService = {
  /**
   * Get all groups for a provider
   */
  async getGroups(
    providerId: string,
    filters?: GroupFilters
  ): Promise<Group[]> {
    let query = supabase
      .from('groups')
      .select('*')
      .eq('provider_id', providerId)
      .order('name', { ascending: true })

    if (filters?.category) {
      query = query.eq('category', filters.category)
    }

    if (filters?.search) {
      query = query.ilike('name', `%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) {
      console.error('Failed to fetch groups:', error)
      throw error
    }

    return data || []
  },

  /**
   * Get all groups with member counts
   */
  async getGroupsWithCounts(providerId: string): Promise<(Group & { member_count: number })[]> {
    const { data: groups, error: groupsError } = await supabase
      .from('groups')
      .select('*')
      .eq('provider_id', providerId)
      .order('name', { ascending: true })

    if (groupsError) {
      console.error('Failed to fetch groups:', groupsError)
      throw groupsError
    }

    if (!groups || groups.length === 0) {
      return []
    }

    // Get member counts for all groups
    const { data: memberCounts, error: countError } = await supabase
      .from('group_members')
      .select('group_id')
      .in('group_id', groups.map(g => g.id))

    if (countError) {
      console.error('Failed to fetch member counts:', countError)
      // Return groups without counts on error
      return groups.map(g => ({ ...g, member_count: 0 }))
    }

    // Count members per group
    const countMap = new Map<string, number>()
    memberCounts?.forEach(m => {
      countMap.set(m.group_id, (countMap.get(m.group_id) || 0) + 1)
    })

    return groups.map(g => ({
      ...g,
      member_count: countMap.get(g.id) || 0,
    }))
  },

  /**
   * Get a single group by ID with its members
   */
  async getGroupById(groupId: string): Promise<GroupWithMembers | null> {
    const { data: group, error: groupError } = await supabase
      .from('groups')
      .select('*')
      .eq('id', groupId)
      .single()

    if (groupError) {
      if (groupError.code === 'PGRST116') return null
      console.error('Failed to fetch group:', groupError)
      throw groupError
    }

    // Fetch group members with client details
    const { data: members, error: membersError } = await supabase
      .from('group_members')
      .select(`
        *,
        client:clients (
          id,
          full_name,
          email,
          status
        )
      `)
      .eq('group_id', groupId)
      .order('joined_at', { ascending: true })

    if (membersError) {
      console.error('Failed to fetch group members:', membersError)
      throw membersError
    }

    const groupMembers = (members || []).map(m => ({
      ...m,
      client: m.client as Pick<Client, 'id' | 'full_name' | 'email' | 'status'>,
    }))

    return {
      ...group,
      members: groupMembers,
      member_count: groupMembers.length,
    }
  },

  /**
   * Create a new group
   */
  async createGroup(group: GroupInsert): Promise<Group> {
    const { data, error } = await supabase
      .from('groups')
      .insert(group)
      .select()
      .single()

    if (error) {
      console.error('Failed to create group:', error)
      throw error
    }

    return data
  },

  /**
   * Update an existing group
   */
  async updateGroup(
    groupId: string,
    updates: GroupUpdate
  ): Promise<Group | null> {
    const { data, error } = await supabase
      .from('groups')
      .update(updates)
      .eq('id', groupId)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to update group:', error)
      throw error
    }

    return data
  },

  /**
   * Delete a group
   */
  async deleteGroup(groupId: string): Promise<boolean> {
    const { error } = await supabase
      .from('groups')
      .delete()
      .eq('id', groupId)

    if (error) {
      console.error('Failed to delete group:', error)
      throw error
    }

    return true
  },

  /**
   * Add a client to a group
   */
  async addMember(
    groupId: string,
    clientId: string,
    role: string = 'member'
  ): Promise<GroupMember> {
    const { data, error } = await supabase
      .from('group_members')
      .insert({
        group_id: groupId,
        client_id: clientId,
        role,
      })
      .select()
      .single()

    if (error) {
      console.error('Failed to add member to group:', error)
      throw error
    }

    return data
  },

  /**
   * Add multiple clients to a group
   */
  async addMembers(
    groupId: string,
    clientIds: string[],
    role: string = 'member'
  ): Promise<GroupMember[]> {
    const membersToInsert: GroupMemberInsert[] = clientIds.map(clientId => ({
      group_id: groupId,
      client_id: clientId,
      role,
    }))

    const { data, error } = await supabase
      .from('group_members')
      .insert(membersToInsert)
      .select()

    if (error) {
      console.error('Failed to add members to group:', error)
      throw error
    }

    return data || []
  },

  /**
   * Remove a client from a group
   */
  async removeMember(groupId: string, clientId: string): Promise<boolean> {
    const { error } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('client_id', clientId)

    if (error) {
      console.error('Failed to remove member from group:', error)
      throw error
    }

    return true
  },

  /**
   * Update a member's role in a group
   */
  async updateMemberRole(
    groupId: string,
    clientId: string,
    role: string
  ): Promise<GroupMember | null> {
    const { data, error } = await supabase
      .from('group_members')
      .update({ role })
      .eq('group_id', groupId)
      .eq('client_id', clientId)
      .select()
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      console.error('Failed to update member role:', error)
      throw error
    }

    return data
  },

  /**
   * Get all members of a group (client IDs only)
   */
  async getGroupMemberIds(groupId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('group_members')
      .select('client_id')
      .eq('group_id', groupId)

    if (error) {
      console.error('Failed to fetch group members:', error)
      throw error
    }

    return (data || []).map(m => m.client_id)
  },

  /**
   * Get groups that a client belongs to
   */
  async getClientGroups(clientId: string): Promise<Group[]> {
    const { data: memberships, error: memberError } = await supabase
      .from('group_members')
      .select('group_id')
      .eq('client_id', clientId)

    if (memberError) {
      console.error('Failed to fetch client memberships:', memberError)
      throw memberError
    }

    if (!memberships || memberships.length === 0) {
      return []
    }

    const { data: groups, error: groupsError } = await supabase
      .from('groups')
      .select('*')
      .in('id', memberships.map(m => m.group_id))
      .order('name', { ascending: true })

    if (groupsError) {
      console.error('Failed to fetch groups:', groupsError)
      throw groupsError
    }

    return groups || []
  },

  /**
   * Get group count for a provider
   */
  async getGroupCount(providerId: string): Promise<number> {
    const { count, error } = await supabase
      .from('groups')
      .select('*', { count: 'exact', head: true })
      .eq('provider_id', providerId)

    if (error) {
      console.error('Failed to get group count:', error)
      return 0
    }

    return count || 0
  },

  /**
   * Get available categories
   */
  getCategories(): { value: string; label: string }[] {
    return [
      { value: 'sports_team', label: 'Sports Team' },
      { value: 'pt_cohort', label: 'PT Cohort' },
      { value: 'gym_class', label: 'Gym Class' },
      { value: 'custom', label: 'Custom' },
    ]
  },
}
